import { useEffect, useState, type ReactNode } from "react";
import { useAuthUser } from "#/api/auth";

type PostHogModule = typeof import("posthog-js");
type PostHogReactModule = typeof import("posthog-js/react");

export function PostHogProvider({ children }: { children: ReactNode }) {
  const [provider, setProvider] = useState<{
    PostHogProvider: PostHogReactModule["PostHogProvider"];
    client: PostHogModule["default"];
  } | null>(null);

  const { data: user } = useAuthUser();

  useEffect(() => {
    const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
    const host =
      import.meta.env.VITE_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";

    if (!key) return;

    void Promise.all([
      import("posthog-js"),
      import("posthog-js/react"),
    ]).then(([posthogMod, reactMod]) => {
      const posthog = posthogMod.default;

      posthog.init(key, {
        api_host: host,
        capture_pageview: true,
        capture_pageleave: true,
      });

      setProvider({
        PostHogProvider: reactMod.PostHogProvider,
        client: posthog,
      });
    });
  }, []);

  useEffect(() => {
    if (!provider) return;

    if (user) {
      provider.client.identify(user.sub, {
        email: user.email,
        name: user.name,
      });
    } else {
      provider.client.reset();
    }
  }, [provider, user]);

  if (!provider) {
    return children;
  }

  return (
    <provider.PostHogProvider client={provider.client}>
      {children}
    </provider.PostHogProvider>
  );
}
